/**
 * FloatingSlot — centralized safe-zone system for ALL fixed/floating controls.
 *
 * ARCHITECTURE RULE (enforced going forward)
 * ------------------------------------------
 *   Any floating / fixed control in Find2Near — FAB, badge, banner, sheet,
 *   crosshair pill, install prompt, feedback launcher, voice/mic cluster, or
 *   AI assistant — MUST wrap itself in <FloatingSlot>. Never re-introduce
 *   bare `fixed bottom-XX right-XX z-[XXXX]` classes on new components.
 *
 *   The tokens driving positioning live in `index.css` (`--f2n-*`) and
 *   respect:
 *     · env(safe-area-inset-*) on all four sides
 *     · mobile bottom-nav height (--f2n-bottom-nav-h flips to 0 on ≥md)
 *     · PWA standalone (extra bottom padding via display-mode media query)
 *     · RTL (uses insetInlineStart / insetInlineEnd, not left/right)
 *     · virtual keyboard (auto-hides opt-in slots via visualViewport)
 *
 * Zones
 * -----
 *   "bottom-right"   → stacked column of FABs (AINA at order 0, voice at 1, …)
 *   "bottom-left"    → stacked column of badges (pending-sync, feedback btn)
 *   "bottom-center"  → banner strip (install prompt, waypoint pill)
 *   "top-right" / "top-left" / "top-center" — for future toasts / breadcrumbs
 *
 * Sizes
 * -----
 *   size="fab" | "cluster" | "badge" | "banner"  — controls the vertical
 *   step used to stack multiple slots in the same zone. Each maps to a CSS
 *   custom property (`--f2n-slot-h-*`) so tuning is one line in index.css.
 *
 * Props
 * -----
 *   zone            — required
 *   order           — 0 = closest to the edge; each +1 stacks one slot up
 *   size            — "fab" (default) | "cluster" | "badge" | "banner"
 *   zIndex          — token key: "fab-primary" | "fab-secondary" | "banner"
 *                     | "badge" | "map-pill" | "sheet" | number. Default
 *                     inferred from `size`.
 *   hideOnKeyboard  — true to auto-hide when the mobile keyboard opens.
 *                     Default true for size="fab"/"cluster", false otherwise.
 *   avoidBottomNav  — true (default) adds --f2n-bottom-nav-h to the offset.
 *   className       — passed through to the wrapper
 *   style           — merged after the computed layout (last-wins)
 *   testid          — data-testid on the wrapper
 */
import React, { useEffect, useState } from "react";

const SLOT_HEIGHT_VAR = {
  fab:     "var(--f2n-slot-h-fab)",
  cluster: "var(--f2n-slot-h-cluster)",
  badge:   "var(--f2n-slot-h-badge)",
  banner:  "var(--f2n-slot-h-banner)",
};

const Z_TOKEN = {
  "fab-primary":    "var(--z-fab-primary)",
  "fab-secondary":  "var(--z-fab-secondary)",
  "banner":         "var(--z-floating-banner)",
  "badge":          "var(--z-floating-badge)",
  "map-pill":       "var(--z-map-pill)",
  "sheet":          "var(--z-sheet)",
  "modal":          "var(--z-modal)",
  "overlay":        "var(--z-overlay)",
};

function defaultZFor(size) {
  if (size === "banner")  return "banner";
  if (size === "badge")   return "badge";
  if (size === "cluster") return "fab-secondary";
  return "fab-primary";
}

/**
 * useKeyboardOpen — best-effort virtual-keyboard detection.
 * Returns true when visualViewport is shrunk more than 150px vs layout
 * viewport (heuristic works across iOS Safari, Android Chrome, Firefox).
 */
function useKeyboardOpen(enabled) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const vv = typeof window !== "undefined" && window.visualViewport;
    if (!vv) return;
    const measure = () => {
      const gap = window.innerHeight - vv.height;
      setOpen(gap > 150);
    };
    measure();
    vv.addEventListener("resize", measure);
    vv.addEventListener("scroll", measure);
    return () => {
      vv.removeEventListener("resize", measure);
      vv.removeEventListener("scroll", measure);
    };
  }, [enabled]);
  return open;
}

// Phones: bottom FABs yield (fade + click-through) while the user scrolls, so they never hide content mid-read.
function useScrollYield(enabled) {
  const [scrolling, setScrolling] = React.useState(false);
  const [covering, setCovering] = React.useState(false);
  React.useEffect(() => {
    if (!enabled) return undefined;
    let t;
    // Also yield (fade + click-through) while an interactive region marked `data-f2n-fab-avoid`
    // sits under the bottom-end FAB column — e.g. the AINA activity chips at 390px.
    const check = () => {
      if (window.innerWidth >= 768) return setCovering(false);
      const rtl = document.documentElement.dir === "rtl";
      const zoneTop = window.innerHeight - 300;
      const hit = [...document.querySelectorAll("[data-f2n-fab-avoid]")].some((el) => {
        const r = el.getBoundingClientRect();
        const edge = rtl ? r.left < 170 : r.right > window.innerWidth - 170;
        return r.bottom > zoneTop && r.top < window.innerHeight && edge;
      });
      setCovering(hit);
    };
    const onScroll = () => {
      if (window.innerWidth >= 768) return;
      setScrolling(true);
      clearTimeout(t);
      t = setTimeout(() => { setScrolling(false); check(); }, 700);
    };
    const first = setTimeout(check, 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", check);
      clearTimeout(t); clearTimeout(first);
    };
  }, [enabled]);
  return scrolling || covering;
}

export default function FloatingSlot({
  zone = "bottom-right",
  order = 0,
  size = "fab",
  zIndex,
  hideOnKeyboard,
  avoidBottomNav = true,
  className = "",
  style,
  testid,
  children,
}) {
  const slotH = SLOT_HEIGHT_VAR[size] || SLOT_HEIGHT_VAR.fab;
  const zKey  = zIndex || defaultZFor(size);
  const z     = typeof zKey === "number" ? zKey : (Z_TOKEN[zKey] || Z_TOKEN["fab-primary"]);

  const keyboardOptIn = hideOnKeyboard ?? (size === "fab" || size === "cluster");
  const keyboardOpen  = useKeyboardOpen(keyboardOptIn);
  const yielding = useScrollYield(zone.startsWith("bottom") && (size === "fab" || size === "cluster"));

  // Vertical stacking:
  //   base = safe-inset + (nav-h if avoidBottomNav) + gap
  //   plus order × (slot-h + gap)
  const navExpr = avoidBottomNav ? " + var(--f2n-bottom-nav-h)" : "";
  const baseBottom = `calc(var(--f2n-safe-bottom)${navExpr} + var(--f2n-slot-gap))`;
  const bottom = order > 0
    ? `calc(${baseBottom} + ${order} * (${slotH} + var(--f2n-slot-gap)))`
    : baseBottom;

  const baseTop = "calc(var(--f2n-safe-top) + var(--f2n-slot-gap))";
  const topOffset = order > 0
    ? `calc(${baseTop} + ${order} * (${slotH} + var(--f2n-slot-gap)))`
    : baseTop;

  const edgePad = "calc(var(--f2n-edge-pad))";
  const layout = { position: "fixed", zIndex: z };

  // NOTE: we use insetInlineStart / insetInlineEnd so [dir="rtl"] flips
  // "bottom-right" → visually the leading edge in the reader's language.
  if (zone === "bottom-right") {
    layout.bottom = bottom;
    layout.insetInlineEnd = `calc(${edgePad} + var(--f2n-safe-right))`;
  } else if (zone === "bottom-left") {
    layout.bottom = bottom;
    layout.insetInlineStart = `calc(${edgePad} + var(--f2n-safe-left))`;
  } else if (zone === "bottom-center") {
    layout.bottom = bottom;
    layout.left = "50%";
    layout.transform = "translateX(-50%)";
    layout.width = `min(calc(100vw - 2 * ${edgePad} - var(--f2n-safe-left) - var(--f2n-safe-right)), 480px)`;
  } else if (zone === "bottom-banner") {
    // Full-width strip (install prompt) — respects safe-area on both sides
    // and centres itself on desktop with a max-width.
    layout.bottom = bottom;
    layout.insetInlineStart = `calc(${edgePad} + var(--f2n-safe-left))`;
    layout.insetInlineEnd   = `calc(${edgePad} + var(--f2n-safe-right))`;
  } else if (zone === "top-right") {
    layout.top = topOffset;
    layout.insetInlineEnd = `calc(${edgePad} + var(--f2n-safe-right))`;
  } else if (zone === "top-left") {
    layout.top = topOffset;
    layout.insetInlineStart = `calc(${edgePad} + var(--f2n-safe-left))`;
  } else if (zone === "top-center") {
    layout.top = topOffset;
    layout.left = "50%";
    layout.transform = "translateX(-50%)";
  }

  return (
    <div
      className={`f2n-floating-slot ${keyboardOptIn ? "f2n-hide-when-keyboard" : ""} ${className}`}
      data-keyboard-open={keyboardOptIn ? String(keyboardOpen) : undefined}
      data-slot-zone={zone}
      data-slot-order={order}
      data-slot-size={size}
      data-yield={yielding ? "true" : undefined}
      data-testid={testid}
      style={{ ...layout, ...(yielding ? { opacity: 0.15, pointerEvents: "none" } : {}),
               transition: "opacity 200ms ease", ...(style || {}) }}
    >
      {children}
    </div>
  );
}
